import React, { useState, useEffect } from "react";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { Button, Title } from "rizzui";
import toast from "react-hot-toast";
import { PiQuestionBold } from "react-icons/pi";
import {
  PolicyConfig,
  defaultPolicyConfig,
} from "@/utilities/constants/defaultCalculatorPolicyConfig";
import SystemSettingRepository from "@/utilities/repositories/SystemSetting";
import { useModal } from "@/app/shared/modal-views/use-modal";
import { FormData, policyConfigSchema } from "./types";
import SidebarNavigation from "./SidebarNavigation";
import CalculatorGuideModal from "./CalculatorGuideModal";

import BSDSection from "./sections/BSDSection";
import ABSDSection from "./sections/ABSDSection";
import LTVSection from "./sections/LTVSection";
import SSDSection from "./sections/SSDSection";

// Helper to normalize config data (handle JSON null vs Infinity)
const normalizeConfig = (config: any): PolicyConfig => {
  if (!config) return defaultPolicyConfig;

  // Create a deep copy to avoid mutating the input
  const normalized = { ...config };

  // Normalize BSD Tiers
  if (Array.isArray(normalized.bsdTiers)) {
    normalized.bsdTiers = normalized.bsdTiers.map((tier: any) => ({
      ...tier,
      maxPrice: tier.maxPrice === null ? Infinity : tier.maxPrice,
    }));
  }

  return normalized;
};

// API functions
const savePolicyConfig = async (
  idCalculator: string,
  config: PolicyConfig,
): Promise<{ config: PolicyConfig; idCalculator: string }> => {
  try {
    await SystemSettingRepository.updateCalculatorPolicy(idCalculator, config);
    // Get the updated config from server
    const response = await SystemSettingRepository.getCalculatorPolicy();
    if (response.data && response.data.data) {
      return { config: normalizeConfig(response.data.data), idCalculator };
    }
    // Fallback to the config we sent if API response is malformed
    return { config, idCalculator };
  } catch (error) {
    console.error("Error saving policy config:", error);
    throw error;
  }
};

const loadPolicyConfig = async (): Promise<{
  savedConfig: PolicyConfig;
  idCalculator: string;
}> => {
  try {
    const response = await SystemSettingRepository.getCalculatorPolicy();
    if (response.data) {
      return {
        savedConfig: normalizeConfig(response.data.data),
        idCalculator: response.data.id,
      };
    }
    return {
      savedConfig: defaultPolicyConfig,
      idCalculator: "",
    };
  } catch (error) {
    console.error("Error loading policy config:", error);
    return {
      savedConfig: defaultPolicyConfig,
      idCalculator: "",
    };
  }
};

const CalculatorConfig: React.FC = () => {
  const [config, setConfig] = useState<PolicyConfig>(defaultPolicyConfig);
  const [idCalculator, setIdCalculator] = useState<string>("");
  const [isLoading, setIsLoading] = useState(false);
  const [activeSection, setActiveSection] = useState<string>("bsd");

  const { openModal } = useModal();

  const openGuide = () => {
    openModal({
      view: <CalculatorGuideModal initialStepId={activeSection} />,
      size: "lg",
    });
  };

  const { control, handleSubmit, reset, formState } = useForm<FormData>({
    resolver: zodResolver(policyConfigSchema),
    defaultValues: config,
    mode: "onChange",
  });

  useEffect(() => {
    (async () => {
      const { savedConfig, idCalculator } = await loadPolicyConfig();
      setIdCalculator(idCalculator);
      // Ensure we always have a valid config
      const validConfig = savedConfig || defaultPolicyConfig;
      setConfig(validConfig);
      reset(validConfig);
    })();
  }, [reset]);

  const onSubmit = async (data: FormData) => {
    setIsLoading(true);
    try {
      // Ensure we have a valid config object
      const currentConfig = config || defaultPolicyConfig;

      const updatedConfig: PolicyConfig = {
        ...currentConfig,
        ...data,
        // Preserve nested objects that aren't in the form
        ssdRates: data.ssdRates,
        effectiveDates: data.effectiveDates,
        propertyFees: currentConfig.propertyFees,
        bucltvRates: currentConfig.bucltvRates,
        paymentStages: currentConfig.paymentStages,
        amortisationDefaults: currentConfig.amortisationDefaults,
        amortisationInterestRatePeriods:
          currentConfig.amortisationInterestRatePeriods,
      };

      // Save to API and get the updated config
      const { config: savedConfig } = await savePolicyConfig(
        idCalculator,
        updatedConfig,
      );
      setConfig(savedConfig);
      reset(savedConfig);

      toast.success("Policy configuration saved successfully!");
    } catch (error) {
      console.error("Error saving policy config:", error);
      toast.error("Error saving policy configuration");
    } finally {
      setIsLoading(false);
    }
  };

  const handleReset = async () => {
    try {
      setIsLoading(true);
      setConfig(defaultPolicyConfig);
      reset(defaultPolicyConfig);
      await savePolicyConfig(idCalculator, defaultPolicyConfig);
      toast.success("Policy configuration reset to defaults!");
    } catch (error) {
      console.error("Error resetting policy config:", error);
      toast.error("Error resetting policy configuration");
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="space-y-4">
      <div className="flex items-center gap-2">
        <Title as="h5" className="font-bold @xl:basis-auto text-xl md:text-2xl">
          Calculator Formulas
        </Title>
        <button
          type="button"
          onClick={openGuide}
          className="text-gray-400 hover:text-primary transition-colors"
          aria-label="Open guide"
          title="Open guide"
        >
          <PiQuestionBold className="h-5 w-5" />
        </button>
      </div>

      {/* Main Content */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Sidebar Navigation */}
        <SidebarNavigation
          activeSection={activeSection}
          onSectionChange={setActiveSection}
        />

        {/* Content Area */}
        <div className="lg:col-span-2 space-y-6">
          {/* Metadata Section */}

          {/* BSD Rates Section */}
          {activeSection === "bsd" && <BSDSection control={control} />}

          {/* ABSD Rates Section */}
          {activeSection === "absd" && <ABSDSection control={control} />}

          {/* LTV Limits Section */}
          {activeSection === "ltv" && <LTVSection control={control} />}

          {/* SSD Rates Section */}
          {activeSection === "ssd" && <SSDSection control={control} />}
        </div>
      </div>
      <div className="flex gap-3 w-full justify-end">
        <Button
          color="danger"
          variant="outline"
          onClick={handleReset}
          disabled={isLoading}
        >
          Reset to Default
        </Button>
        <Button
          onClick={handleSubmit(onSubmit, (error) => {
            console.log("Error saving policy config:", error);
          })}
          isLoading={isLoading}
          disabled={!formState.isDirty}
        >
          Save Changes
        </Button>
      </div>
    </div>
  );
};

export default CalculatorConfig;
